import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import { saveGuestWithGoogle } from "src/store/auth"
import { addMessage } from "src/store/messages"
import { claimGuest, selectUsername } from "src/store/user"

import Modal from "src/components/modals/Modal"
import GoogleSignInButton from "src/components/GoogleSignInButton"
import { apiErrorLines, fieldError } from "src/utils/apiErrors"
import { playerFacingError } from "src/utils/errorMessages"
import { USERNAME_MAX_LENGTH, usernameProblem } from "src/utils/username"

/* A refusal with a `code` is one of ours and is worded in the player's language; anything else is
   DRF's field errors (a weak password, a malformed address), printed against their fields. */
export const saveErrorLines = (errors, { except = [] } = {}) =>
  errors?.code ? [{ key: "code", text: playerFacingError(errors.code) }] : apiErrorLines(errors, { except })

/* Save a guest account (owner, 2026-10-02): an address and a password on the same row, so the
   games, the points and the credits stay — or Google, which saves it the same way when that Google
   account is nobody's here and otherwise signs in to the account it already opens. The name is
   optional: a guest may keep the funny one, and this first choice of their own is free. */
const SaveGuestModal = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const currentName = useSelector(selectUsername)

  const [errors, setErrors] = useState(null)
  // One save at a time: a second tap on the way posted twice, and the second answer was
  // `NotAGuest` flashed over the success (see `PlayAsGuestButton`, which guards the same way).
  const [busy, setBusy] = useState(false)
  const [formData, setFormData] = useState({ username: "", email: "", password: "" })
  const nameProblem = formData.username ? usernameProblem(formData.username) : null
  // A weak password is said under its input, not again at the top (gamekit 0.11.0's 400 `{password}`).
  const passwordProblem = errors?.code ? null : fieldError(errors, "password")

  const saved = (text) => {
    dispatch(addMessage({ text, type: "success" }))
    ModalService.hideAll()
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (busy) return
    const payload = { email: formData.email, password: formData.password }
    if (formData.username.trim()) payload.username = formData.username.trim()
    setBusy(true)
    dispatch(claimGuest(payload))
      .unwrap()
      .then(() => saved(t("guest.saved")))
      .catch((error) => {
        setErrors(error)
        setBusy(false)
      })
  }
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    setErrors(null)
  }

  const handleGoogleCredential = (credential) => {
    if (busy) return
    setErrors(null)
    setBusy(true)
    dispatch(saveGuestWithGoogle(credential))
      .unwrap()
      .then(({ switched }) => saved(switched ? t("guest.switched") : t("guest.saved")))
      .catch((error) => {
        setErrors(error)
        setBusy(false)
      })
  }

  return (
    <Modal>
      <h1>{t("guest.saveTitle")}</h1>
      <p className="p-mini">{t("guest.saveHint")}</p>
      {saveErrorLines(errors, { except: ["password"] }).map(({ key, text }) => (
        <p key={key} className="errored-text">
          {text}
        </p>
      ))}
      <form onSubmit={handleSubmit}>
        <label htmlFor="guest-username">{t("guest.usernameOptional")}</label>
        <input
          type="text"
          id="guest-username"
          name="username"
          className="form-input"
          placeholder={currentName || ""}
          value={formData.username}
          onChange={handleInputChange}
          maxLength={USERNAME_MAX_LENGTH}
        />
        {nameProblem && <p className="errored-text">{t(nameProblem.key, nameProblem.values)}</p>}

        <label htmlFor="guest-email">{t("auth.emailLabel")}</label>
        <input
          type="email"
          id="guest-email"
          name="email"
          className="form-input"
          placeholder={t("auth.emailPlaceholder")}
          value={formData.email}
          onChange={handleInputChange}
          required
        />

        <label htmlFor="guest-password">{t("auth.passwordLabel")}</label>
        <input
          type="password"
          id="guest-password"
          name="password"
          className="form-input"
          placeholder={t("auth.passwordPlaceholder")}
          value={formData.password}
          onChange={handleInputChange}
          aria-invalid={Boolean(passwordProblem)}
          required
        />
        {passwordProblem && <p className="errored-text">{passwordProblem}</p>}

        <div className="modal-actions">
          <button type="submit" disabled={busy || Boolean(nameProblem)}>
            {t("guest.saveButton")}
          </button>
        </div>
      </form>

      <GoogleSignInButton onCredential={handleGoogleCredential} text="continue_with" />
    </Modal>
  )
}

export default SaveGuestModal
